import React from "react";
import {
  Download,
  FileAudio,
  FileText,
  Image as ImageIcon,
  Paperclip,
  Play,
  Sticker,
  Video
} from "lucide-react";

import "./whatsapp.css";

const MIME_DOCUMENT = "application/pdf";

export function getWhatsAppMediaKind(message = {}) {
  const type = String(message.type || "").toLowerCase();
  const mime = String(message.mimeType || "").toLowerCase();
  const filename = String(message.filename || "").toLowerCase();

  if (type === "sticker") return "sticker";
  if (type === "image" || mime.startsWith("image/")) return "image";
  if (type === "video" || mime.startsWith("video/")) return "video";
  if (type === "audio" || mime.startsWith("audio/")) return "audio";
  if (
    type === "file" ||
    type === "document" ||
    mime === MIME_DOCUMENT ||
    mime.startsWith("application/") ||
    filename.includes(".")
  ) {
    return "document";
  }

  return "text";
}

function formatBytes(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return "Archivo adjunto";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExtension(message) {
  const name = String(message.filename || "");
  const extension = name.includes(".") ? name.split(".").pop() : "FILE";
  return extension.slice(0, 5).toUpperCase();
}

function messageTime(timestamp) {
  const date = timestamp?.toDate
    ? timestamp.toDate()
    : timestamp instanceof Date
      ? timestamp
      : timestamp
        ? new Date(timestamp)
        : null;

  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function MediaPreview({ message, kind, onOpen }) {
  const url = message.fileUrl || message.url || message.mediaUrl;
  const filename = message.filename || "Archivo adjunto";

  if (!url) {
    return (
      <div className="vd-wa-media-missing">
        <Paperclip size={18} />
        <span>Adjunto no disponible</span>
      </div>
    );
  }

  if (kind === "image") {
    return (
      <button className="vd-wa-image-preview" type="button" onClick={onOpen}>
        <img src={url} alt={filename || "Imagen recibida"} loading="lazy" />
        <span className="vd-wa-preview-badge">
          <ImageIcon size={14} /> Ver imagen
        </span>
      </button>
    );
  }

  if (kind === "video") {
    return (
      <div className="vd-wa-video-preview">
        <video controls preload="metadata" playsInline src={url} />
        <div className="vd-wa-media-caption">
          <Video size={15} />
          <span>{filename}</span>
        </div>
      </div>
    );
  }

  if (kind === "audio") {
    return (
      <div className="vd-wa-audio-preview">
        <div className="vd-wa-audio-icon"><Play size={17} fill="currentColor" /></div>
        <audio controls preload="metadata" src={url} />
        <div className="vd-wa-media-caption">
          <FileAudio size={14} />
          <span>{filename}</span>
        </div>
      </div>
    );
  }

  if (kind === "sticker") {
    return (
      <button className="vd-wa-sticker-preview" type="button" onClick={onOpen}>
        <img src={url} alt="Sticker recibido" loading="lazy" />
        <span className="vd-wa-sticker-label"><Sticker size={13} /> Sticker</span>
      </button>
    );
  }

  return (
    <button className="vd-wa-document-preview" type="button" onClick={onOpen}>
      <div className="vd-wa-document-icon"><FileText size={22} /></div>
      <div className="vd-wa-document-info">
        <strong>{filename}</strong>
        <span>{fileExtension(message)} · {formatBytes(message.fileSize)}</span>
        <em>Vista previa disponible · Abrir archivo</em>
      </div>
      <Download size={18} className="vd-wa-document-action" />
    </button>
  );
}

export function WhatsAppMessageBubble({ message = {}, onOpenFile, compact = false }) {
  const kind = getWhatsAppMediaKind(message);
  const hasMedia = kind !== "text";
  const openFile = () => {
    if (!onOpenFile || !message.fileUrl) return;
    onOpenFile(message.fileUrl, message.filename, message.mimeType);
  };
  const caption = message.caption || (hasMedia ? "" : message.body) || "";

  return (
    <div className={`vd-wa-row ${message.fromMe ? "is-outgoing" : "is-incoming"}`}>
      <article className={`vd-wa-bubble ${hasMedia ? "has-media" : ""} ${compact ? "is-compact" : ""}`}>
        {hasMedia ? <MediaPreview message={message} kind={kind} onOpen={openFile} /> : (
          <div className="vd-wa-text">{caption || "Mensaje vacío"}</div>
        )}

        {hasMedia && caption ? <div className="vd-wa-caption">{caption}</div> : null}

        <footer className="vd-wa-meta">
          <span>{messageTime(message.timestamp)}</span>
          {message.fromMe ? <span className="vd-wa-status" data-status={message.status || "sent"}>
            {message.status === "sending" ? "Enviando…" : message.status === "failed" ? "No enviado" : "✓ Enviado"}
          </span> : null}
        </footer>
      </article>
    </div>
  );
}

export function WhatsAppAvatar({ conversation, size = 44 }) {
  const [failed, setFailed] = React.useState(false);
  const name = conversation?.patientName || conversation?.name || "Prospecto";
  const photo = conversation?.profilePicUrl || conversation?.profilePhoto || conversation?.photoURL || conversation?.photoUrl || conversation?.profilePicture;
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase() || "P";

  return (
    <span className="vd-wa-avatar" style={{ width: size, height: size }}>
      {photo && !failed ? <img src={photo} alt={`Foto de ${name}`} onError={() => setFailed(true)} /> : initials}
    </span>
  );
}

export function WhatsAppThread({ messages = [], onOpenFile, bottomRef, compact = false }) {
  const visibleMessages = messages.filter(message => {
    const kind = getWhatsAppMediaKind(message);
    return kind !== "text" || Boolean(message.body || message.caption);
  });

  return (
    <div className={`vd-wa-thread ${compact ? "is-compact" : ""}`}>
      {visibleMessages.length ? visibleMessages.map(message => (
        <WhatsAppMessageBubble key={message.id || message.externalId || `${message.timestamp}-${message.body}`} message={message} onOpenFile={onOpenFile} compact={compact} />
      )) : (
        <div className="vd-wa-empty-thread">
          <div className="vd-wa-empty-orb"><MessagePlaceholder /></div>
          <strong>Tu conversación aparecerá aquí</strong>
          <span>Envía un mensaje o espera la respuesta del paciente.</span>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

function MessagePlaceholder() {
  return <span aria-hidden="true">✦</span>;
}

